import { Inject, Injectable } from '@angular/core';
import { CollabFileType } from '@common/collab/enums/collab-file-type.enum';
import { CollabCommitMergeResult, CollabCommitResult } from '@common/collab/models/collab-commit-result.model';
import { CollabDashboardData } from '@common/collab/models/collab-dashboard-data.model';
import { CollabDiscardResult } from '@common/collab/models/collab-discard-result.model';
import { CollabDocCommitRequestBody } from '@common/collab/models/collab-doc-commit-request-body.model';
import { CollabDocContentRequestBody } from '@common/collab/models/collab-doc-content-request-body.model';
import { CollabDocCreateRequestBody } from '@common/collab/models/collab-doc-create-request-body.model';
import { CollabDocDeleteRequestBody } from '@common/collab/models/collab-doc-delete-request-body.model';
import { CollabDocDiscardChangesRequestBody } from '@common/collab/models/collab-doc-discard-changes-request-body.model';
import { CollabDocPullOrFetchRequestBody } from '@common/collab/models/collab-doc-pull-or-fetch-request-body.model';
import { CollabDocPullRequestBody } from '@common/collab/models/collab-doc-pull-request-body.model';
import { CollabDocRedisJSON } from '@common/collab/models/collab-doc-redis-json.model';
import { CollabDocRenameRequestBody } from '@common/collab/models/collab-doc-rename-request-body.model';
import { CollabDocRestoreVersionRequestBody } from '@common/collab/models/collab-doc-restore-version-request-body.model';
import { CollabDocStepChunkRequestBody } from '@common/collab/models/collab-doc-step-chunk-request-body.model';
import { CollabDocStepChunk } from '@common/collab/models/collab-doc-step-chunk.model';
import { ProjectFileParameters, ReviewFileParameters } from '@common/collab/types/doc-parameters.type';
import { DomCompareResult } from '@common/html/types/html-compare.type';
import { MadCloudResult } from '@common/http/models/mad-cloud-result.model';
import { ProjectFileSystemType } from '@common/project-files/enums/project-file-system-type.enum';
import { StepJSON } from '@common/prosemirror/transform/json';
import { basename } from '@common/util/path';
import { joinWithCommasAnd } from '@common/util/string';
import { CollabApiService } from '@portal-core/collab/services/collab-api.service';
import { StringService } from '@portal-core/general/services/string.service';
import { SnackBarAutoDismissDurationMS } from '@portal-core/ui/snack-bar/constants/snack-bar-auto-dismiss-duration.constant';
import { WINDOW } from '@portal-core/util/window.provider';
import { map, Observable, of, switchMap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class CollabService {
  constructor(@Inject(WINDOW) private window: Window, private collabApiService: CollabApiService, private stringService: StringService) { }

  getDashboardData$(): Observable<CollabDashboardData> {
    return this.collabApiService.getDashboardData$();
  }

  getCollabDoc$(fileType: CollabFileType, fileParams: ProjectFileParameters | ReviewFileParameters): Observable<CollabDocRedisJSON> {
    return this.collabApiService.getCollabDoc$(fileType, fileParams);
  }

  getCollabDocByDocId$(docId: string, schemaName: string): Observable<CollabDocRedisJSON> {
    return this.collabApiService.getCollabDocByDocId$(docId, schemaName);
  }

  getCollabDocStepChunk$(body: CollabDocStepChunkRequestBody): Observable<CollabDocStepChunk> {
    return this.collabApiService.getCollabDocStepChunk$(body);
  }

  getCollabDocContent$(body: CollabDocContentRequestBody): Observable<string> {
    return this.collabApiService.getCollabDocContent$(body).pipe(
      map(content => content ? this.stringService.stripBOM(content) : null)
    );
  }

  commitCollabDocs$(body: CollabDocCommitRequestBody): Observable<CollabCommitResult> {
    return this.collabApiService.commitCollabDocs$(body);
  }

  createCollabDoc$(body: CollabDocCreateRequestBody): Observable<void> {
    return this.collabApiService.createCollabDoc$(body);
  }

  deleteCollabDoc$(body: CollabDocDeleteRequestBody): Observable<CollabCommitResult> {
    return this.collabApiService.deleteCollabDoc$(body);
  }

  discardChangesFromCollabDocs$(body: CollabDocDiscardChangesRequestBody): Observable<CollabDiscardResult[]> {
    return this.collabApiService.discardChangesFromCollabDocs$(body);
  }

  openDocContentPath(projectId: number, branchName: string, filePath: string) {
    this.window.open(this.collabApiService.getDocContentPath(projectId, branchName, filePath), '_blank');
  }

  pullCollabDoc$(body: CollabDocPullRequestBody): Observable<CollabCommitMergeResult> {
    return this.collabApiService.pullCollabDoc$(body);
  }

  pullOrFetchCollabDocs$(body: CollabDocPullOrFetchRequestBody): Observable<CollabCommitMergeResult> {
    return this.collabApiService.pullOrFetchCollabDocs$(body);
  }

  renameCollabDoc$(body: CollabDocRenameRequestBody): Observable<CollabCommitResult> {
    return this.collabApiService.renameCollabDoc$(body);
  }

  restoreCollabDocToVersion$(body: CollabDocRestoreVersionRequestBody): Observable<{ docId: string, invertedSteps: StepJSON[], steps: StepJSON[] }> {
    return this.collabApiService.restoreCollabDocToVersion$(body);
  }

  roundtrip$(code: string, schemaName: string): Observable<MadCloudResult<DomCompareResult>> {
    return this.collabApiService.roundtrip$(code, schemaName);
  }

  /** Returns the file name of the Flare project from the workspace file tree. */
  getFlareProjectFileName$(projectId: number, branchName: string): Observable<string> {
    const projectFileExt = '.flprj';
    return this.collabApiService.getWorkspaceFileTree$(projectId, branchName, '', true, projectFileExt, true).pipe(
      map(items => {
        const files = items.filter(item => item.Type === ProjectFileSystemType.File);
        return files.length ? files[0].Name : null;
      })
    );
  }

  /** Returns the decoded string content of the Flare project file from the collab server. */
  getFlareProjectFileContent$(projectId: number, branchName: string): Observable<string> {
    return this.getFlareProjectFileName$(projectId, branchName).pipe(
      switchMap(fileName => {
        if (!fileName) {
          return of(null);
        }
        return this.getCollabDocContent$({
          branchName,
          filePath: fileName,
          fileType: CollabFileType.Workspace,
          fromRedis: true,
          projectId,
          reviewFileId: null
        });
      })
    );
  }

  buildPullResultSnackBarMessage(commitMergeResult: CollabCommitMergeResult): { duration: number, message: string, deleted: boolean } {
    const deletedFiles = (commitMergeResult?.conflictResolutionResult?.deleted ?? []).filter(mergeResult => {
      return mergeResult.success;
    });

    // Group the merged and pending adds files together
    const mergedFiles = [
      ...(commitMergeResult?.conflictResolutionResult?.merged ?? []),
      ...(commitMergeResult?.conflictResolutionResult?.pendingAdds ?? [])
    ].filter(mergeResult => {
      return mergeResult.success;
    });

    let message: string = '';
    let duration = SnackBarAutoDismissDurationMS;

    // Build the message for the deleted files
    if (deletedFiles.length > 0) {
      if (deletedFiles.length < 5) {
        message = joinWithCommasAnd(deletedFiles.map(file => `"${basename(file.filePath)}"`));
        duration = deletedFiles.length === 1 ? SnackBarAutoDismissDurationMS : SnackBarAutoDismissDurationMS * 2; // Show the message for longer if there are multiple file paths being displayed
      } else {
        message = `${deletedFiles.length} files`;
      }

      message += (deletedFiles.length === 1 ? ' was' : ' were') + ' deleted in an external commit.';
    }

    // Build the message for the merged files
    if (mergedFiles.length > 0) {
      message += (message ? ' And the' : 'The') + ' latest changes have been merged into ';

      if (mergedFiles.length < 5) {
        message += joinWithCommasAnd(mergedFiles.map(file => `"${basename(file.filePath)}"`));
        duration = mergedFiles.length === 1 ? SnackBarAutoDismissDurationMS : SnackBarAutoDismissDurationMS * 2; // Show the message for longer if there are multiple file paths being displayed
      } else {
        message += `${mergedFiles.length} files`;
      }

      message += '.';
    }

    return {
      deleted: deletedFiles.length > 0,
      duration, message
    };
  }
}
